/**
 * Resolve the auth-gated node images of a Cytoscape graph.
 *
 * `node[imageUrl]` feeds the stylesheet's `background-image`, which Cytoscape
 * loads through a bare image request — no `Authorization` header, so every
 * `/api/v1/files/...` crop comes back 401. `nodeToCytoscapeElement` therefore
 * parks those paths on `authedImageUrl` and leaves `imageUrl` unset; this pass
 * fetches them with the token through the shared blob cache and only then
 * writes `imageUrl`, so the stylesheet always gets a directly loadable URL.
 */
import type { Core, NodeSingular } from 'cytoscape'
import type { AuthedImageCache } from '@/lib/authed-image-cache'

/**
 * Fill in `imageUrl` for every node that still carries an unresolved
 * `authedImageUrl`. Safe to call repeatedly — resolved nodes are skipped and
 * the cache de-duplicates concurrent fetches of the same crop.
 */
export async function resolveAuthedNodeImages(cy: Core, cache: AuthedImageCache): Promise<void> {
  const pending = cy
    .nodes()
    .filter((node: NodeSingular) => Boolean(node.data('authedImageUrl')) && !node.data('imageUrl'))

  await Promise.all(
    pending.map(async (node: NodeSingular) => {
      const source = String(node.data('authedImageUrl'))
      const cached = cache.peek(source)
      if (cached) {
        applyImage(cy, node.id(), cached)
        return
      }
      const objectUrl = await cache.resolve(source)
      if (objectUrl) applyImage(cy, node.id(), objectUrl)
    })
  )
}

/** Write the blob URL back, unless the node vanished while we were fetching. */
function applyImage(cy: Core, nodeId: string, objectUrl: string): void {
  if (cy.destroyed()) return
  const node = cy.getElementById(nodeId)
  if (node.empty()) return
  node.data('imageUrl', objectUrl)
}
